import { roundCurrency, toDecimal } from "@/lib/utils/math";
import { calculateCompoundInterest } from "./compound-interest";

export interface RetirementInput {
  currentAge: number;
  retirementAge: number;
  currentSavings: number;
  monthlyContribution: number;
  expectedReturn: number;
  /** Inflation % for real (inflation-adjusted) withdrawal (default 0) */
  inflationRate?: number;
}

export interface RetirementResult {
  /** Projected balance at retirement */
  balanceAtRetirement: number;
  /** Total contributions made over the period */
  totalContributions: number;
  /** Growth from investment returns */
  totalGrowth: number;
  /** Monthly withdrawal at 4% rule (nominal) */
  monthlyWithdrawal: number;
  /** Monthly withdrawal inflation-adjusted when inflationRate > 0 */
  realMonthlyWithdrawal?: number;
  /** Years until retirement */
  yearsToRetirement: number;
  chartData: { year: number; age: number; balance: number }[];
}

const WITHDRAWAL_RATE = 0.04; // 4% rule

export function calculateRetirement(
  input: RetirementInput
): RetirementResult {
  const {
    currentAge,
    retirementAge,
    currentSavings,
    monthlyContribution,
    expectedReturn,
    inflationRate = 0,
  } = input;

  const yearsToRetirement = Math.max(0, retirementAge - currentAge);

  if (yearsToRetirement <= 0) {
    return {
      balanceAtRetirement: currentSavings,
      totalContributions: 0,
      totalGrowth: 0,
      monthlyWithdrawal: roundCurrency(
        toDecimal(currentSavings * WITHDRAWAL_RATE / 12)
      ),
      realMonthlyWithdrawal:
        inflationRate > 0
          ? roundCurrency(
              toDecimal(
                (currentSavings * WITHDRAWAL_RATE) /
                  12 /
                  Math.pow(1 + inflationRate / 100, yearsToRetirement)
              )
            )
          : undefined,
      yearsToRetirement: 0,
      chartData: [{ year: 0, age: currentAge, balance: currentSavings }],
    };
  }

  const compoundResult = calculateCompoundInterest({
    principal: currentSavings,
    rate: expectedReturn,
    years: yearsToRetirement,
    frequency: 12,
    contribution: monthlyContribution,
    contributionFrequency: 12,
  });

  const balanceAtRetirement = compoundResult.total;
  const totalContributions =
    compoundResult.totalContributions ?? monthlyContribution * 12 * yearsToRetirement;
  const totalGrowth = compoundResult.interest;

  const monthlyWithdrawal = roundCurrency(
    toDecimal((balanceAtRetirement * WITHDRAWAL_RATE) / 12)
  );

  const realMonthlyWithdrawal =
    inflationRate > 0
      ? roundCurrency(
          toDecimal(
            (balanceAtRetirement * WITHDRAWAL_RATE) /
              12 /
              Math.pow(1 + inflationRate / 100, yearsToRetirement)
          )
        )
      : undefined;

  const chartData = compoundResult.chartData.map((d) => ({
    year: d.year,
    age: currentAge + d.year,
    balance: d.total,
  }));

  return {
    balanceAtRetirement,
    totalContributions,
    totalGrowth,
    monthlyWithdrawal,
    realMonthlyWithdrawal,
    yearsToRetirement,
    chartData,
  };
}
